/**
 * Tour state machine (#776). Pure: no React, no storage, no DOM, so it is unit-tested on its own.
 *
 *   idle ──start──▶ running(i) ──next──▶ running(i+1) … ──next on last / finish──▶ finished
 *                       │  ◀──back──
 *                       └──skip──▶ skipped
 *
 * Persisted form: { v: 1, status, index }. "finished" and "skipped" mean "never auto-start again"; "running" means the
 * tour was interrupted (reload, closed tab) and resumes at `index` on the next visit. Replay (Help menu) starts at 0.
 */
export type TourStatus = 'idle' | 'running' | 'finished' | 'skipped';

export interface TourState {
  status: TourStatus;
  index: number;
  total: number;
}

export type TourAction =
  | { type: 'start'; index?: number }
  | { type: 'next' }
  | { type: 'back' }
  | { type: 'goto'; index: number }
  | { type: 'skip' }
  | { type: 'finish' };

export function initialTourState(total: number): TourState {
  return { status: 'idle', index: 0, total };
}

const clamp = (i: number, total: number) => Math.min(Math.max(0, Math.trunc(i)), Math.max(0, total - 1));

export function tourReducer(state: TourState, action: TourAction): TourState {
  const { total } = state;
  if (total <= 0) return state;
  switch (action.type) {
    case 'start':
      return { ...state, status: 'running', index: clamp(action.index ?? 0, total) };
    case 'next':
      if (state.status !== 'running') return state;
      return state.index >= total - 1 ? { ...state, status: 'finished' } : { ...state, index: state.index + 1 };
    case 'back':
      if (state.status !== 'running' || state.index === 0) return state;
      return { ...state, index: state.index - 1 };
    case 'goto':
      if (state.status !== 'running') return state;
      return { ...state, index: clamp(action.index, total) };
    case 'skip':
      return state.status === 'running' ? { ...state, status: 'skipped' } : state;
    case 'finish':
      return state.status === 'running' ? { ...state, status: 'finished' } : state;
  }
}

export interface PersistedTour {
  v: 1;
  status: Exclude<TourStatus, 'idle'>;
  index: number;
}

export function toPersisted(state: TourState): PersistedTour | null {
  if (state.status === 'idle') return null;
  return { v: 1, status: state.status, index: state.index };
}

/** Parses the stored value; anything unreadable counts as "never seen" (null). */
export function parsePersisted(raw: string | null | undefined): PersistedTour | null {
  if (!raw) return null;
  try {
    const p = JSON.parse(raw) as Partial<PersistedTour> | null;
    if (!p || p.v !== 1) return null;
    if (p.status !== 'running' && p.status !== 'finished' && p.status !== 'skipped') return null;
    const index = typeof p.index === 'number' && Number.isFinite(p.index) ? p.index : 0;
    return { v: 1, status: p.status, index };
  } catch {
    return null;
  }
}

/** What to do on page load: auto-start on a first visit, resume an interrupted tour, otherwise stay idle. */
export function initialAction(persisted: PersistedTour | null, total: number): TourAction | null {
  if (total <= 0) return null;
  if (!persisted) return { type: 'start', index: 0 };
  if (persisted.status === 'running') return { type: 'start', index: clamp(persisted.index, total) };
  return null;
}
